//Copyright (c) 2024 Greener Power Solutions.
//This file is part of Greener Power Solutions' software,
//and may not be altered, copied, redistributed, or sold
//without specific permission.
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Button, CircularProgress, Grid, Stack, Typography } from '@mui/material';
import { Breadcrumb } from 'components/layout/Breadcrumb';
import { useFavouriteProjects } from 'features/favourites/hooks/useFavouriteProjects/useFavouriteProjects';
import { ProjectsFilterSection } from 'features/filters/components/ProjectsFilterSection';
import { useGetOrganisationProjects } from 'features/projects/hooks/useGetOrganisationProjects';
import { Icons, ToggleButtonGroup } from 'greener-ui-library';
import useAuth from 'hooks/useAuth';
import { useBreakpoint } from 'hooks/useBreakpoint';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { debounce } from 'lodash';
import { useIntersectionObserver } from 'usehooks-ts';
import { canCreateOrganisation, canCreateProjects } from 'utils/authorization';
import { projectsRoutes } from './Projects.routes';
import { PROJECTS_VIEWS, getComingProjects, getCurrentProjects, getPastProjects } from './Projects.utils';

const PROJECTS_PER_PAGE_MAP = {
  xs: 3,
  sm: 6,
  md: 12,
  lg: 18,
  xl: 42,
};

const MAX_MAP_PROJECTS = 150;
const LIST_VIEW_MAX_ROWS = 100; //this is a limitation coming from free version of the dataTable library we use for list view

export const ProjectsTopbarLayout = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { search } = useLocation();
  const { width, isMobile } = useBreakpoint();
  const [projectsView, setProjectView] = useLocalStorage('projectView', PROJECTS_VIEWS.CARD);

  const userOrganisationId = user?.organisation.organisationId;

  const {
    projects: myProjects,
    isLoading,
    isFetching,
    hasNextPage,
    goToNextPage,
  } = useGetOrganisationProjects({
    organisationId: userOrganisationId,
    perPage: projectsView === PROJECTS_VIEWS.MAP ? MAX_MAP_PROJECTS : PROJECTS_PER_PAGE_MAP[width], // fetch everything for map view (no infinite scroll)
  });

  const isLoadingOrFetching = isLoading || isFetching;
  const isListViewAtLimit = projectsView === PROJECTS_VIEWS.LIST && (myProjects?.length ?? 0) >= LIST_VIEW_MAX_ROWS;
  const canFetchMore = hasNextPage && !isListViewAtLimit;

  const handleIntersection = useMemo(() => {
    const debouncedFn = debounce(
      ({ isIntersecting, canFetchMore }: { isIntersecting: boolean; canFetchMore?: boolean }) => {
        if (isIntersecting && !isLoadingOrFetching && canFetchMore && !!goToNextPage) {
          goToNextPage();
        }
      },
      200
    ); // small debounce to avoid loading immidiately on first load

    return debouncedFn;
  }, [goToNextPage, isLoadingOrFetching]);

  const { ref, isIntersecting } = useIntersectionObserver({
    threshold: 1.0,
  });

  const { favouriteProjects } = useFavouriteProjects();

  useEffect(() => {
    handleIntersection({ isIntersecting, canFetchMore });
  }, [handleIntersection, isIntersecting, canFetchMore]);

  const currentProjects = useMemo(() => getCurrentProjects(myProjects ?? []), [myProjects]);

  const pastProjects = useMemo(() => getPastProjects(myProjects ?? []), [myProjects]);

  const futureProjects = useMemo(() => getComingProjects(myProjects ?? []), [myProjects]);

  const allProjects = useMemo(
    () => [...currentProjects, ...futureProjects, ...pastProjects],
    [currentProjects, futureProjects, pastProjects]
  );

  const toggleViewButtonContent = [
    {
      value: PROJECTS_VIEWS.CARD,
      content: <Icons.SpaceDashboardOutlined />,
      buttonProps: { onClick: () => navigate({ pathname: PROJECTS_VIEWS.CARD, search }) },
    },
    {
      value: PROJECTS_VIEWS.LIST,
      content: <Icons.ViewHeadline />,
      buttonProps: { onClick: () => navigate({ pathname: PROJECTS_VIEWS.LIST, search }) },
    },
    {
      value: PROJECTS_VIEWS.MAP,
      content: <Icons.LocationOn />,
      buttonProps: { onClick: () => navigate({ pathname: PROJECTS_VIEWS.MAP, search }) },
    },
  ];

  return (
    <Grid container direction="row">
      <Breadcrumb routes={projectsRoutes} />

      <Grid item container alignItems="center" justifyContent="space-between">
        <Typography variant="h1">{t('entities:project.namePlural')}</Typography>
        {user && canCreateProjects(user.role) && (
          <Grid item>
            <Button
              variant="outlined"
              data-testid="new-project-button"
              onClick={() => navigate('/projects/new')}
              sx={isMobile ? { minWidth: 0, p: 1 } : null}
            >
              {isMobile ? <Icons.Plus /> : t('projects:overview.buttons.newProject')}
            </Button>
          </Grid>
        )}
      </Grid>

      <Grid
        container
        item
        py={4}
        spacing={2}
        alignItems={isMobile ? 'center' : 'flex-start'}
        justifyContent={'space-between'}
      >
        <ProjectsFilterSection displayOrganisationFilter={canCreateOrganisation(user?.role)} />
        <Grid item container xs={isMobile ? 4 : 6} md={1} justifyContent={'flex-end'}>
          <ToggleButtonGroup
            selectedValue={projectsView}
            buttons={toggleViewButtonContent}
            exclusive
            onChange={(value) => setProjectView(value as string)}
          />
        </Grid>
      </Grid>
      <Outlet
        context={{
          allProjects,
          projectsInApiOrder: myProjects ?? [],
          favouriteProjects: favouriteProjects ?? [],
          ref,
          isLoading,
        }}
      />
      <Stack flex={1} p={3}>
        <CircularProgress
          sx={{
            visibility: isLoadingOrFetching ? 'visible' : 'hidden', //adjust visibility instead of not rendering to avoid layout jumps
            alignSelf: 'center',
          }}
          size={20}
        />
      </Stack>
      {/* No infinite fetching for map view */}
      {projectsView !== PROJECTS_VIEWS.MAP && (
        <div
          ref={ref}
          data-testid="infinite-scroll-sentinel"
          style={{
            height: '1px',
            width: '100%',
          }}
        />
      )}
    </Grid>
  );
};
